// 自由な色（自前の色選び）（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { txt, txtHtml } from './text.js';
import { keyOf } from './util.js';
import { hexToRgb } from './colormath.js';
import { setColor } from './palette.js';
import { histCut } from './history.js';
import { closeModal, openModalNode } from './dialog.js';
import type { Category } from './types.js';
// ---- 自由な色（自前の色選び） ----
// 前はブラウザの部品（input type=color）だったが、iPhone の部品（グリッド・スペクトラム・スライダ）はスペクトラムが指で選びにくく、
// 開くタブもページから選べないので、自前にした（ユーザー 2026-10-12）。上の四角であざやかさ（横）・明るさ（縦）、下の帯で色合い（HSV）。
// 色の番号（#rrggbb）でも入れられる。動かしている間は色だけ付け（setColor の quiet）、閉じる（「決定」・外を押す・Esc）と一覧を描き直して
// 最近使った色に入れる。元に戻すは選び始めで区切るので、開いてから閉じるまでで1回分
export function hexToHsv(hex: string) {
  const c = hexToRgb(hex).map((v) => v / 255);
  let max = Math.max(c[0], c[1], c[2]), min = Math.min(c[0], c[1], c[2]), d = max - min, h = 0;
  if (d) {
    if (max === c[0]) h = ((c[1] - c[2]) / d) % 6;
    else if (max === c[1]) h = (c[2] - c[0]) / d + 2;
    else h = (c[0] - c[1]) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h: h, s: max ? d / max : 0, v: max };
}
export function hsvToHex(h: number, sat: number, val: number) {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return Math.round((val - val * sat * Math.max(0, Math.min(k, 4 - k, 1))) * 255);
  };
  return '#' + [f(5), f(3), f(1)].map((v) => ('0' + v.toString(16)).substr(-2)).join('');
}
export function openColorPicker(cat: Category, now: string | null, key?: string) { // key: 塗るカテゴリ（既定は選んでいるカテゴリ。palette.ts の setColor）
  const start = /^#[0-9a-f]{6}$/.test(now || '') ? now!
    : S.grad && S.grad.key === keyOf(cat) ? (S.grad.sel.to && !S.grad.sel.from ? S.grad.to : S.grad.from)
    : (cat.COLOR[0] || '#888888').toLowerCase();
  var hsv = hexToHsv(start), cur = start, started = false, raf = 0;
  const root = document.createElement('div');
  root.className = 'cpick';
  root.innerHTML = '<h2>' + txtHtml('color_custom') + '</h2>' +
    '<div class="cpick-sv" role="slider" tabindex="0" aria-label="' + txtHtml('picker_sv') + '"><i class="cpick-dot"></i></div>' +
    '<div class="cpick-hue" role="slider" tabindex="0" aria-label="' + txtHtml('picker_hue') + '" aria-valuemin="0" aria-valuemax="360"><i class="cpick-knob"></i></div>' +
    '<div class="cpick-row"><span class="cpick-prev"></span><input class="cpick-hex" type="text" maxlength="7" inputmode="text" spellcheck="false" autocomplete="off" autocapitalize="off" aria-label="' + txtHtml('picker_hex') + '"></div>';
  var sv = root.querySelector<HTMLElement>('.cpick-sv')!, dot = root.querySelector<HTMLElement>('.cpick-dot')!, hue = root.querySelector<HTMLElement>('.cpick-hue')!, knob = root.querySelector<HTMLElement>('.cpick-knob')!;
  var prev = root.querySelector<HTMLElement>('.cpick-prev')!, hexIn = root.querySelector<HTMLInputElement>('.cpick-hex')!;
  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  function draw(withHex?: boolean) {
    sv.style.backgroundColor = hsvToHex(hsv.h, 1, 1);
    dot.style.left = (hsv.s * 100) + '%';
    dot.style.top = ((1 - hsv.v) * 100) + '%';
    dot.style.background = cur;
    knob.style.left = (hsv.h / 360 * 100) + '%';
    knob.style.background = hsvToHex(hsv.h, 1, 1);
    hue.setAttribute('aria-valuenow', Math.round(hsv.h));
    sv.setAttribute('aria-valuetext', cur);
    prev.style.background = cur;
    if (withHex !== false) hexIn.value = cur;
  }
  // 選び始め: 元に戻すの区切り（それまでの変更と分ける）
  function begin() { if (!started) { histCut(); started = true; } }
  // キャラに付けるのは1フレームに1回
  function apply() {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; setColor(cur, true, key); });
  }
  function change(h: number, sat: number, val: number, withHex?: boolean) {
    hsv = { h: h, s: sat, v: val };
    cur = hsvToHex(h, sat, val);
    begin();
    draw(withHex);
    apply();
  }
  function drag(el: HTMLElement, at: (e: PointerEvent) => void) {
    el.addEventListener('pointerdown', (e) => { e.preventDefault(); el.focus({ preventScroll: true }); el.setPointerCapture(e.pointerId); at(e); });
    el.addEventListener('pointermove', (e) => { if (el.hasPointerCapture(e.pointerId)) at(e); });
  }
  drag(sv, (e) => { const r = sv.getBoundingClientRect(); change(hsv.h, clamp((e.clientX - r.left) / r.width), clamp(1 - (e.clientY - r.top) / r.height)); });
  drag(hue, (e) => { const r = hue.getBoundingClientRect(); change(clamp((e.clientX - r.left) / r.width) * 360, hsv.s, hsv.v); });
  // キーボード: 四角は矢印であざやかさ・明るさ、帯は左右で色合い
  sv.addEventListener('keydown', (e) => {
    const d = ({ ArrowLeft: [-0.02, 0], ArrowRight: [0.02, 0], ArrowUp: [0, 0.02], ArrowDown: [0, -0.02] } as Record<string, number[]>)[e.key];
    if (!d) return;
    e.preventDefault();
    change(hsv.h, clamp(hsv.s + d[0]), clamp(hsv.v + d[1]));
  });
  hue.addEventListener('keydown', (e) => {
    const d = ({ ArrowLeft: -3, ArrowDown: -3, ArrowRight: 3, ArrowUp: 3 } as Record<string, number>)[e.key];
    if (!d) return;
    e.preventDefault();
    change((hsv.h + d + 360) % 360, hsv.s, hsv.v);
  });
  // 色の番号: 6桁そろったら（# は無くてもよい）。色の無い灰色のときは、色合いを今のまま残す
  hexIn.addEventListener('input', () => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hexIn.value.trim());
    if (!m) return;
    const next = hexToHsv('#' + m[1].toLowerCase());
    change(next.s ? next.h : hsv.h, next.s, next.v, false);
  });
  hexIn.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); closeModal(); } });
  hexIn.addEventListener('blur', () => { hexIn.value = cur; });
  openModalNode(root, [], { closeLabel: txt('ok'), light: true, cls: 'picker', onClose: () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } if (started) setColor(cur, false, key); } });
  draw();
}
